import { useEffect, useState, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import { Button, Container, Col, Row, Modal } from 'react-bootstrap'
import parse from 'html-react-parser'

import { getAffirmationData } from '../../../common/api/Practices'
import BlobAnimation from '../../BlobAnimation'
import Loading from '../../../../templates/Loading'
import { ROUTE_ACTIVITIES_AFFIRMATIONS_ALL } from '../../../common/helpers/Paths'
import {
  buildRequestURL,
  KEY_PRACTICE_AFFIRMATIONS_WP,
  REMBLE_API_URL
} from '../../../common/api'
import {
  saveUserEvent,
  EVENT_CATEGORY_AFFIRMATION,
  EVENT_ACTION_PAGEVIEW
} from '../../../common/api/UserEvents'
import {
  postMobileShareToInstagram,
  isInApp
} from '../../../common/helpers/Mobile'
import './AffirmationFullScreen.scss'
import WebShare from 'components/web-share'
import Bookmark from 'components/bookmark/Bookmark'

// Presets for the affirmation sharing modal
const presets = [
  {
    name: 'Green',
    background: 'None',
    watermark: 'wordmark-white',
    background_color: '#2e933c',
    background_gradient: '#2e933c',
    text_color: '#ffffff',
    text_size: 78,
    text_shadow_size: 10,
    text_alignment: 'center',
    top_color: '#2e933c',
    bottom_color: '#2e933c'
  },
  {
    name: 'Bubbles',
    background: 'waimakariri',
    watermark: 'wordmark-black',
    background_color: '#ffffff',
    background_gradient: '#ffffff',
    text_color: '#000000',
    text_size: 78,
    text_shadow_size: 0,
    text_alignment: 'center',
    top_color: '#ffffff',
    bottom_color: '#ffffff'
  },
  {
    name: 'Black',
    background: 'None',
    watermark: 'wordmark-white',
    background_color: '#000000',
    background_gradient: '#000000',
    text_color: '#ffffff',
    text_size: 78,
    text_shadow_size: 0,
    text_alignment: 'center',
    top_color: '#000000',
    bottom_color: '#000000'
  }
]

/**
 * Affirmation Full Screen component. Used for displaying full-screen affirmations
 * @param {Object} paramObj Dependency object
 */
export default function AffirmationFullScreen({
  setHeaderTitle,
  setReturnPath
}) {
  const { affirmationId } = useParams()
  const [prompts, setPrompts] = useState([])
  const [activeIndex, setActiveIndex] = useState()
  const [previousIndex, setPreviousIndex] = useState()
  const [isLoading, setIsLoading] = useState(true)
  const [showSharingModal, setShowSharingModal] = useState()
  const [selectedPreset, setSelectedPreset] = useState(1)

  // For local storage
  const storageKey = 'activeAffirmationIndex' + affirmationId

  /**
   * Helper function to generate the image URL for the affirmation
   */
  const generateUrl = useCallback((text, presetIndex) => {
    // Return early if we don't have a valid value
    if (!text || presetIndex === undefined) return
    const baseUrl = `${REMBLE_API_URL}/practices/affirmations/create`
    const props = { ...presets[presetIndex], text }
    return encodeURI(buildRequestURL(baseUrl, props))
  }, [])

  /**
   * Helper function to update current and previous index
   * @param {Number} next The next index
   * @returns null
   */
  const updateIndex = next => {
    // Return early if this is our initialization run or if the index is the same
    if (activeIndex === undefined || next === activeIndex) return

    setActiveIndex(next)
    setPreviousIndex(activeIndex)

    // Save for later
    localStorage.setItem(storageKey, next)
  }

  /**
   * // Helper function to generate a span with the props we need
   * @param {Object} param0 An object with the props we need
   * @returns An string representation of an HTML span element
   */
  const _span = ({ text, index, isPrevious }) => {
    const style = `animation-delay: ${index * (isPrevious ? 0.01 : 0.1)}s`
    const addBreak = text.indexOf('/') > -1
    const cleanText = text.replace('/', '')

    // NOTE that this is stitched together as a string instead of interpolated as a component,
    // so that it can be parsed as HTML later
    return `<span class='span' key='${index}' style='${style}'>${cleanText}&nbsp;</span>${
      addBreak ? '<br />' : ''
    }`
  }

  /**
   * Helper function to split text and then rejoin it with a span wrapper
   * If we have a slash `/` in the text, wrap the text with a div instead of a span
   * @param {*} text The text to split
   * @param {*} isPrevious Whether this is the previous text
   * @returns A string of HTML with spans wrapped around the text
   */
  const spanify = (text, isPrevious) =>
    text
      .split(' ')
      .map((text, index) => _span({ text, index, isPrevious }))
      .join('')

  /**
   * Helper function to get the proper class, depending on the affirmation state. Looks to see if the
   * affirmation is active, and if it is, returns the active class. If it is not, returns the inactive class.
   * @param {Number} index The index of the affirmation
   * @returns The class name
   */
  const getClassName = index => {
    let name = 'affirmation'

    if (index === activeIndex) name += ' active'
    else if (index === previousIndex) name += ' previous'

    return name
  }

  /**
   * Helper function to show the next affirmation
   */
  const showNext = () => {
    let next = activeIndex + 1

    // Out of bounds, reset
    if (next >= prompts.length) next = 0

    updateIndex(next)
  }

  /**
   * Helper function to show the previous affirmation
   */
  const showPrevious = () => {
    let previous = activeIndex - 1

    // Out of bounds, go to end
    if (previous < 0) previous = prompts.length - 1

    updateIndex(previous)
  }

  /**
   * Helper function to show the share modal
   */
  const shareAffirmation = () => {
    setShowSharingModal(true)
  }

  /**
   * Helper function to share the affirmation to Instagram
   * @returns null
   */
  const shareToInstagram = () => {
    const activePreset = presets[selectedPreset]
    const activePrompt = prompts[activeIndex]
    const { top_color, bottom_color } = activePreset
    const image = generateUrl(activePrompt, selectedPreset)

    // Return early if we don't have valid values
    if (!image || !top_color || !bottom_color) return

    // Send the payload to the iOS app
    postMobileShareToInstagram({ image, top_color, bottom_color })
  }

  useEffect(() => {
    // Scroll to top
    window.scrollTo(0, 0)

    // Set header and return path
    setHeaderTitle(null)
    setReturnPath(ROUTE_ACTIVITIES_AFFIRMATIONS_ALL)

    async function getAffirmationDataAsync() {
      const result = await getAffirmationData(affirmationId)
      const reformattedArray = result.map(obj => obj.post_title) || []
      const active = Number(localStorage.getItem(storageKey)) || 0

      setPrompts(reformattedArray)
      setIsLoading(false)
      setActiveIndex(active)

      // Log access
      saveUserEvent(
        EVENT_CATEGORY_AFFIRMATION,
        EVENT_ACTION_PAGEVIEW,
        affirmationId
      )
    }
    getAffirmationDataAsync()
  }, [affirmationId, storageKey, generateUrl, setHeaderTitle, setReturnPath])

  return (
    <>
      <Loading isLoaded={isLoading}>
        <Container className='affirmation-fullscreen-container'>
          <Bookmark
            categoryObjectId={affirmationId}
            category={KEY_PRACTICE_AFFIRMATIONS_WP}
            color='text-black'
          />
          <WebShare
            text={prompts[activeIndex]}
            size='fs-5'
            buttonClass='position-absolute video-share-button'
            color='text-black'
          />
          <BlobAnimation />
          <>
            <nav className='affirmation-navigation'>
              <Row>
                <Col>
                  <span className='nav-item' onClick={() => showPrevious()}>
                    <i className='bi bi-arrow-left'></i> Back
                  </span>
                </Col>
                <Col className='text-right'>
                  <span className='nav-item' onClick={() => showNext()}>
                    Next <i className='bi bi-arrow-right'></i>
                  </span>
                </Col>
              </Row>
            </nav>
            {prompts.length > 1 && (
              <>
                {prompts.map((content, index) => (
                  <div className={getClassName(index)} key={index}>
                    <h1 className='affirmation-text'>
                      {parse(spanify(content, index === previousIndex))}
                    </h1>
                    <div className='actions'>
                      <span
                        className='action'
                        onClick={_ => shareAffirmation()}
                      >
                        <i className='bi bi-share'></i>
                      </span>
                    </div>
                  </div>
                ))}
              </>
            )}
          </>
        </Container>
      </Loading>
      <Modal
        centered
        show={showSharingModal}
        onHide={_ => setShowSharingModal(false)}
        className='affirmation-modal'
      >
        <Modal.Header
          closeButton={`${
            document.body.classList.contains('dark') ? 'white' : 'black'
          }`}
          closeVariant={`${
            document.body.classList.contains('dark') ? 'white' : ''
          }`}
        >
          <Modal.Title>Share affirmation</Modal.Title>
        </Modal.Header>
        <Modal.Body className='text-center'>
          <h3>Preview</h3>
          <div className='affirmation-preview'>
            <img
              loading='lazy'
              src={generateUrl(prompts[activeIndex], selectedPreset)}
              alt={prompts[activeIndex]}
            />
          </div>
          <p>
            {isInApp()
              ? 'Select a style below, then tap the share button.'
              : 'Select a style below, then right-click to save or copy this image to share it'}
          </p>

          <h4 className='mt-4'>Pick a style</h4>
          <Row className='affirmation-presets'>
            {presets.map((preset, presetIndex) => (
              <Col key={presetIndex}>
                <span className='affirmation-preview-label'>{preset.name}</span>
                <img
                  loading='lazy'
                  className={`affirmation-preview-thumbnail ${
                    presetIndex === selectedPreset ? 'active' : ''
                  }`}
                  src={generateUrl(prompts[activeIndex], presetIndex)}
                  alt={prompts[activeIndex]}
                  onClick={_ => setSelectedPreset(presetIndex)}
                  width='100%'
                />
              </Col>
            ))}
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Row>
            {isInApp() && (
              <Col xs={8}>
                <Button
                  className='affirmation-share-button'
                  variant='secondary'
                  onClick={_ => shareToInstagram()}
                >
                  Share to Instagram <i className='bi bi-instagram mx-1'></i>
                </Button>
              </Col>
            )}

            <Col xs={4}>
              <Button
                variant='primary'
                onClick={_ => setShowSharingModal(false)}
              >
                Done
              </Button>
            </Col>
          </Row>
        </Modal.Footer>
      </Modal>
    </>
  )
}
